import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Card from '../components/Card.jsx';
import Button from '../components/Button.jsx';
import {
  getInvitationCategory,
  invitationCategories,
} from '../lib/invitationCatalog.js';
import { invitationMeta } from '../seo/invitationMeta.js';
import '../styles/Invitations.css';

const Invitations = () => {
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');
  const [filter, setFilter] = useState(categoryParam || 'all');
  const [publicInvitations, setPublicInvitations] = useState([]);
  const [isLoadingInvitations, setIsLoadingInvitations] = useState(true);
  const [hasLoadedPublicInvitations, setHasLoadedPublicInvitations] = useState(false);

  useEffect(() => {
    setFilter(getInvitationCategory(categoryParam)?.value || 'all');
  }, [categoryParam]);

  // Fetch public invitations from completed bookings only.
  useEffect(() => {
    let mounted = true
    async function loadPublic() {
      try {
        setIsLoadingInvitations(true);
        const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api';
        const res = await fetch(`${API_BASE}/bookings/completed-invitations`)

        if (!mounted) return

        if (!res.ok) {
          setPublicInvitations([])
          return
        }

        const json = await res.json()

        if (!mounted) return

        const completedInvitations = Array.isArray(json?.invitations)
          ? json.invitations.filter((inv) => !inv?.status || inv.status === 'completed')
          : [];

        if (json?.ok) {
          // Transform API data to match catalog format
          const enriched = completedInvitations.map((inv, index) => {
            const slug = inv.linked_invitation_id || `booking-${inv.id}`;

            // Default preview -> custom route. Prefer canonical paths from invitationMeta when available.
            let previewPath = `/invitations/custom/${slug}`;
            if (inv.linked_invitation_id) {
              const slug = String(inv.linked_invitation_id);

              // Try exact substring match first (preserve previous behavior)
              let metaEntry = invitationMeta.find((m) => m.path && m.path.includes(slug));

              // If not found, normalize both the DB slug and the canonical meta slug
              // by removing non-alphanumeric chars and compare the last path segment.
              if (!metaEntry) {
                const normalizedSlug = slug.toLowerCase().replace(/[^a-z0-9]/g, '');
                metaEntry = invitationMeta.find((m) => {
                  if (!m.path) return false;
                  const parts = m.path.split('/').filter(Boolean);
                  const last = parts[parts.length - 1] || '';
                  const normalizedMeta = String(last).toLowerCase().replace(/[^a-z0-9]/g, '');
                  return normalizedMeta === normalizedSlug;
                });
              }

              if (metaEntry && metaEntry.path) previewPath = metaEntry.path;
            }

            return {
              id: slug,
              title: inv.celebrant_name || 'Custom Invitation',
              category: inv.event_type?.toLowerCase() || 'other',
              categoryLabel: inv.event_type || 'Event',
              slug,
              image: `/og/${slug}.jpg`,
              description: `Completed ${inv.event_type || 'event'} invitation for ${inv.celebrant_name || 'our client'}.`,
              price: 'P800',
              previewPath,
              order: index + 1,
              categories: [inv.event_type?.toLowerCase() || 'other'],
            };
          });
          setPublicInvitations(enriched)
        } else {
          setPublicInvitations([])
        }
      } catch (err) {
        if (mounted) {
          console.error('Failed to load completed invitations:', err);
          setPublicInvitations([])
        }
      } finally {
        if (mounted) {
          setHasLoadedPublicInvitations(true);
          setIsLoadingInvitations(false);
        }
      }
    }
    loadPublic()
    return () => { mounted = false }
  }, [])

  const designsSource = publicInvitations;
  const filteredDesigns = filter === 'all'
    ? designsSource
    : designsSource.filter((t) => (t.category === filter || (t.categories && t.categories.includes(filter))));

  return (
    <div className="invitations">
      {/* Hero Section */}
      <div className="invitations-hero">
        <div className="invitations-hero-content">
          <span className="invitations-badge">Digital Invitations Philippines</span>
          <h1 className="invitations-hero-title">
            Beautiful Digital Invitations<br />for Every Special Moment
          </h1>
          <p className="invitations-hero-description">
            Create stunning, mobile-friendly invitations that your guests will love. 
            From weddings to birthdays, we've got every celebration covered with 
            professional designs and seamless RSVP tracking.
          </p>
          
          <div className="invitations-hero-stats">
            <div className="stat-item">
              <span className="stat-number">{isLoadingInvitations && !hasLoadedPublicInvitations ? '...' : publicInvitations.length}</span>
              <span className="stat-label">Completed Designs</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">₱800</span>
              <span className="stat-label">Starting Price</span>
            </div>
            <div className="stat-item">
              <span className="stat-number">2-3</span>
              <span className="stat-label">Days Delivery</span>
            </div>
          </div>

          <div className="invitations-hero-actions">
            <Link to="/book">
              <Button variant="primary" size="large">
                Book Now
              </Button>
            </Link>
            <button 
              className="hero-secondary-btn"
              onClick={() => document.querySelector('.templates-section-label')?.scrollIntoView({ behavior: 'smooth' })}
            >
              Browse Designs ↓
            </button>
          </div>
        </div>
      </div>

      {/* Features Section */}
      <section className="invitations-features">
        <div className="invitations-features-grid">
          <div className="feature-card">
            <div className="feature-icon">📱</div>
            <h3>Mobile-Optimized</h3>
            <p>Every design looks perfect on all devices, ensuring your guests have the best experience.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">⚡</div>
            <h3>Quick Sharing</h3>
            <p>Share via Messenger, Facebook, email, or any platform with just one link.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">✅</div>
            <h3>RSVP Tracking</h3>
            <p>Built-in RSVP system to easily track your guests' responses in real-time.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🎨</div>
            <h3>Customizable</h3>
            <p>Choose from our designs or request a fully personalized invitation.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">💰</div>
            <h3>Affordable</h3>
            <p>Professional quality starting at just ₱800 with optional add-ons.</p>
          </div>
          <div className="feature-card">
            <div className="feature-icon">🚀</div>
            <h3>Fast Delivery</h3>
            <p>Get your invitation ready in 2-3 days, perfect for time-sensitive events.</p>
          </div>
        </div>
      </section>

      {/* Design Gallery Header */}
      <div className="templates-section-label">
        <h2>Browse Designs</h2>
        <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap' }}>
          <span className="templates-count">{filteredDesigns.length} design{filteredDesigns.length !== 1 ? 's' : ''} available</span>
          {hasLoadedPublicInvitations && (
            <span className="db-indicator" title="Showing completed invitations from database">
              ✓ Completed Bookings
            </span>
          )}
        </div>
      </div>
      <div className="filter-section">
        <div className="filter-buttons">
          {invitationCategories.map((cat) => (
            <button
              key={cat.value}
              className={`filter-btn ${filter === cat.value ? 'active' : ''}`}
              onClick={() => setFilter(cat.value)}
            >
              {cat.icon && <span aria-hidden="true" className="filter-btn-icon">{cat.icon}</span>}
              {cat.label}
            </button>
          ))}
        </div>
      </div>

      <div className="templates-grid">
        {isLoadingInvitations ? (
          <div className="empty-state">
            <div className="empty-state-content">
              <span className="empty-state-icon">⏳</span>
              <h3>Loading Invitations...</h3>
              <p>Fetching the latest completed designs from our database.</p>
            </div>
          </div>
        ) : filteredDesigns.length === 0 ? (
          <div className="empty-state">
            <div className="empty-state-content">
              <span className="empty-state-icon">🎨</span>
              <h3>No Completed Invitations Available Yet</h3>
              <p>Completed booking designs will appear here once they are ready.</p>
              <Link to="/book">
                <Button variant="primary" size="large">
                  Create Custom Invitation
                </Button>
              </Link>
            </div>
          </div>
        ) : (
          filteredDesigns.map((design) => (
            <div key={design.id || design.invitationId} className="template-card-wrapper">
              {design.previewPath && (
                <a href={design.previewPath} className="preview-overlay" target="_blank" rel="noopener noreferrer">
                  <span className="preview-text">Click to Preview</span>
                </a>
              )}
              <Card
                title={design.title}
                description={design.description}
                image={design.image}
                className="template-card"
                category={design.category}
                categoryLabel={design.categoryLabel || getInvitationCategory(design.category)?.label || design.category}
              />
            </div>
          ))
        )}
      </div>

      {/* Why Choose Us Section */}
      <section className="invitations-benefits">
        <div className="benefits-content">
          <h2>Why Choose Digital Invitations?</h2>
          <p className="benefits-lead">
            Modern, eco-friendly, and incredibly convenient for you and your guests.
          </p>
          
          <div className="benefits-grid">
            <div className="benefit-item">
              <div className="benefit-icon">💨</div>
              <h3>Instant Delivery</h3>
              <p>No printing, no shipping. Share your invitation instantly with all your guests.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">🌍</div>
              <h3>Eco-Friendly</h3>
              <p>Go paperless and reduce your event's environmental footprint.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">📊</div>
              <h3>Easy RSVP Management</h3>
              <p>Track responses in real-time and manage your guest list effortlessly.</p>
            </div>
            <div className="benefit-item">
              <div className="benefit-icon">✏️</div>
              <h3>Quick Updates</h3>
              <p>Need to change event details? Update your invitation anytime.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Custom Design CTA */}
      <section className="invitations-cta">
        <div className="cta-content">
          <h2>Need Something Unique?</h2>
          <p>
            Can't find the perfect design? Let us create a custom invitation tailored to your event's theme and style.
          </p>
          <div className="cta-actions">
            <Link to="/book?type=custom">
              <Button variant="primary" size="large">
                Request Custom Design
              </Button>
            </Link>
            <Link to="/digital-invitation-faq" className="cta-link">
              View FAQ →
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Invitations;
